import React from "react";
import { Avatar, Box, CircularProgress, IconButton, OutlinedInput, Stack, Typography } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import ShieldIcon from "@mui/icons-material/Shield";
import ApiService from "../../services/api-service";
import notifier from "../Notifier";
import { fullName, getErrorString, getUserId } from "../../constants/function";

/**
 * A host's moderators: people trusted to run the jukebox and remove audience
 * members in this host's streams. The list lives on the host, so it carries
 * from one stream to the next and can be edited before going live.
 */
export default function ModeratorsPicker({ channelName }) {
  const [moderators, setModerators] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [search, setSearch] = React.useState("");
  const [results, setResults] = React.useState([]);
  const [searching, setSearching] = React.useState(false);
  const [busyId, setBusyId] = React.useState(null);
  const myId = getUserId();

  const load = React.useCallback(async () => {
    try {
      const res = await ApiService.LIVESTREAM_MODERATORS_LIST();
      setModerators(res?.result?.moderators || []);
    } catch (e) {
      notifier.error(getErrorString(e));
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    load();
  }, [load]);

  // Wait for a pause in typing rather than firing a request per keystroke.
  React.useEffect(() => {
    const keyword = search.trim();
    if (keyword.length < 2) {
      setResults([]);
      setSearching(false);
      return undefined;
    }
    let alive = true;
    setSearching(true);
    const timer = setTimeout(async () => {
      try {
        const res = await ApiService.ADMIN_SEARCH_USER(keyword);
        if (alive) setResults(res?.result || []);
      } catch (e) {
        if (alive) setResults([]);
      } finally {
        if (alive) setSearching(false);
      }
    }, 350);
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [search]);

  const isModerator = (id) => moderators.some((m) => String(m._id) === String(id));

  const add = async (user) => {
    setBusyId(String(user._id));
    try {
      await ApiService.LIVESTREAM_MODERATOR_ADD(user._id, channelName);
      await load();
      setSearch("");
      notifier.success(`${fullName(user)} can moderate your streams`);
    } catch (e) {
      notifier.error(getErrorString(e));
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (user) => {
    setBusyId(String(user._id));
    try {
      await ApiService.LIVESTREAM_MODERATOR_REMOVE(user._id, channelName);
      await load();
    } catch (e) {
      notifier.error(getErrorString(e));
    } finally {
      setBusyId(null);
    }
  };

  const nameOf = (u) => u?.displayName || fullName(u);

  const row = (u, action) => (
    <Stack
      key={String(u._id)}
      direction="row"
      alignItems="center"
      spacing={1.5}
      sx={{ padding: "8px 10px", borderRadius: "10px", background: "#1A1A1A" }}
    >
      <Avatar src={u.profileImg?.filePath || u.profileImg || ""} sx={{ width: 30, height: 30 }}>
        {(nameOf(u) || "?")[0]}
      </Avatar>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: 14, color: "#fff", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {nameOf(u)}
        </Typography>
        {u.userName ? (
          <Typography sx={{ fontSize: 11, color: "rgba(255,255,255,0.45)" }}>@{u.userName}</Typography>
        ) : null}
      </Box>
      {busyId === String(u._id) ? <CircularProgress size={18} /> : action}
    </Stack>
  );

  return (
    <Stack spacing={1.25} sx={{ marginTop: "8px" }}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <ShieldIcon sx={{ fontSize: 16, color: "#FFEE20" }} />
        <Typography sx={{ fontSize: 14, fontWeight: 700, color: "#fff" }}>Moderators</Typography>
      </Stack>
      <Typography sx={{ fontSize: 12, color: "rgba(255,255,255,0.5)" }}>
        They can run the jukebox and remove people from your streams. They cannot start or end one.
      </Typography>

      {loading ? (
        <Box sx={{ textAlign: "center", padding: "12px" }}>
          <CircularProgress size={20} />
        </Box>
      ) : moderators.length > 0 ? (
        <Stack spacing={0.75}>
          {moderators.map((m) =>
            row(
              m,
              <IconButton size="small" aria-label={`Remove ${nameOf(m)} as moderator`} onClick={() => remove(m)}>
                <CloseIcon sx={{ fontSize: 16, color: "rgba(255,255,255,0.6)" }} />
              </IconButton>
            )
          )}
        </Stack>
      ) : (
        <Typography sx={{ fontSize: 12, color: "rgba(255,255,255,0.35)" }}>No moderators yet.</Typography>
      )}

      <OutlinedInput
        fullWidth
        size="small"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search members to add"
        inputProps={{ "aria-label": "Search members to add as a moderator" }}
        startAdornment={<SearchIcon sx={{ color: "rgba(255,255,255,0.4)", fontSize: 18, mr: 1 }} />}
        endAdornment={searching ? <CircularProgress size={16} /> : null}
        sx={{
          borderRadius: "25px",
          background: "#1A1A1A",
          color: "#fff",
          "& fieldset": { borderColor: "rgba(255,255,255,0.12)" },
        }}
      />

      {search.trim().length >= 2 && !searching ? (
        results.filter((u) => String(u._id) !== String(myId) && !isModerator(u._id)).length > 0 ? (
          <Stack spacing={0.75} sx={{ maxHeight: 180, overflowY: "auto" }}>
            {results
              .filter((u) => String(u._id) !== String(myId) && !isModerator(u._id))
              .map((u) =>
                row(
                  u,
                  <IconButton size="small" aria-label={`Make ${nameOf(u)} a moderator`} onClick={() => add(u)}>
                    <AddIcon sx={{ fontSize: 18, color: "#FFEE20" }} />
                  </IconButton>
                )
              )}
          </Stack>
        ) : (
          <Typography sx={{ fontSize: 12, color: "rgba(255,255,255,0.35)" }}>
            No one else matches "{search.trim()}".
          </Typography>
        )
      ) : null}
    </Stack>
  );
}
